import type { FormDataAdapter } from "$lib/util"
import { addressAdapter, addressSchema } from "$lib/yup/address"
import { phone, phoneNumberAdapter } from "$lib/yup/util/phone-number"
import {
  Address,
  TRUSTED_CONTACT_COUNTRIES_OF_RESIDENCE,
  TrustedContact,
  USA_CITIZENSHIP_COUNTRY,
  ValidateYup,
} from "@tastyworks/tastyworks-api"
import type { Alpha3Code } from "i18n-iso-countries/index"
import { boolean, object, string, type InferType } from "yup"

export const addTrustedContactSchema = object({
  addTrustedContact: boolean().required(),
})

export const trustedContactPersonalInfoSchema =
  ValidateYup.trustedContactSchema.pick(["firstName", "lastName", "birthDate"])

export const trustedContactContactInfoSchema = ValidateYup.trustedContactSchema
  .pick(["email"])
  .concat(
    object({
      phoneNumber: phone().required(),
    })
  )

export const trustedContactAddressSchema = object({
  address: addressSchema.concat(
    object({
      country: string<Alpha3Code>()
        .required("Required")
        .oneOf(Array.from(TRUSTED_CONTACT_COUNTRIES_OF_RESIDENCE))
        .default(USA_CITIZENSHIP_COUNTRY),
    })
  ),
})

export const trustedContactSchema = object({
  addTrustedContact: addTrustedContactSchema,
  trustedContactPersonalInfo: trustedContactPersonalInfoSchema,
  trustedContactContactInfo: trustedContactContactInfoSchema,
  trustedContactAddress: trustedContactAddressSchema,
})

export type TrustedContactFormData = InferType<typeof trustedContactSchema>
export type TrustedContactPageData =
  TrustedContactFormData[keyof TrustedContactFormData]

export const trustedContactPersonalInfoAdapter: FormDataAdapter<
  TrustedContact,
  TrustedContactFormData["trustedContactPersonalInfo"]
> = {
  toForm: (trustedContact, formData) => {
    formData.firstName = trustedContact.firstName
    formData.lastName = trustedContact.lastName
    formData.birthDate = trustedContact.birthDate || undefined

    return formData
  },
  toModel: (formData, trustedContact) => {
    trustedContact.firstName = formData.firstName
    trustedContact.lastName = formData.lastName
    trustedContact.birthDate = formData.birthDate

    return trustedContact
  },
  toPatch: (formData) => {
    const partial: Partial<TrustedContact> = {}

    partial.firstName = formData.firstName
    partial.lastName = formData.lastName
    partial.birthDate = formData.birthDate

    return partial
  },
}

export const trustedContactContactInfoAdapter: FormDataAdapter<
  TrustedContact,
  TrustedContactFormData["trustedContactContactInfo"]
> = {
  toForm: (trustedContact, formData) => {
    formData.email = trustedContact.email
    formData.phoneNumber = phoneNumberAdapter.toForm(trustedContact.phoneNumber)

    return formData
  },
  toModel: (formData, trustedContact) => {
    trustedContact.email = formData.email
    trustedContact.phoneNumber = phoneNumberAdapter.toModel(
      formData.phoneNumber
    )

    return trustedContact
  },
  toPatch: (formData) => {
    const partial: Partial<TrustedContact> = {}

    partial.email = formData.email
    partial.phoneNumber = phoneNumberAdapter.toModel(formData.phoneNumber)

    return partial
  },
}

export const trustedContactAddressAdapter: FormDataAdapter<
  TrustedContact,
  TrustedContactFormData["trustedContactAddress"]
> = {
  toForm: (trustedContact, formData) => {
    if (!formData.address) formData.address = new Address()
    addressAdapter.toForm(trustedContact.address, formData.address)

    return formData
  },
  toModel: (formData, trustedContact) => {
    addressAdapter.toModel(formData.address, trustedContact.address)

    return trustedContact
  },
  toPatch: (formData) => {
    const partial: Partial<TrustedContact> = { address: new Address() }

    addressAdapter.toModel(formData.address, partial.address!)

    return partial
  },
}
